import * as React from "react";

import {type TDataFromGqlOperation} from "@khan/data-gql-core";

import ApolloMutation from "~/data-access/gql/apollo-mutation.tsx";

import {ARCHIVE_ASSIGNMENTS_MUTATION} from "./queries.ts";
import type {Assignment} from "./types.ts";

export interface ArchiveContentAssignmentsResult {
    isArchiving: boolean;
}

export type ArchiveContentAssignmentsAction = (ids: Array<string>) => void;

interface Props {
    children: (
        archiveAssignmentsAction: ArchiveContentAssignmentsAction,
        data: ArchiveContentAssignmentsResult,
    ) => React.ReactNode;
    onSuccess: (archivedAssignments: ReadonlyArray<Assignment>) => void;
    onError: (error: Error) => void;
}

const ArchiveContentAssignmentsMutation = (props: Props) => {
    const handleMutationSuccess = (
        data?:
            | TDataFromGqlOperation<typeof ARCHIVE_ASSIGNMENTS_MUTATION>
            | null
            | undefined,
    ) => {
        props.onSuccess(
            // NOTE: `Assignment` contains a number of fields that are not present
            // in the `archiveContentAssignments` mutation data.  Removing these
            // fields causes TypeScript to report a lot of type errors.  I'm not
            // sure how code that uses this data is working without these fields.
            (data?.archiveAssignments?.assignments ??
                []) as unknown as ReadonlyArray<Assignment>,
        );
    };

    const handleMutationError = (error: Error) => {
        props.onError(error);
    };

    return (
        <ApolloMutation
            mutation={ARCHIVE_ASSIGNMENTS_MUTATION}
            onCompleted={handleMutationSuccess}
            onError={handleMutationError}
        >
            {(archiveAssignments, result) =>
                props.children(
                    (assignmentIds: Array<string>) => {
                        archiveAssignments({
                            variables: {
                                assignmentIds,
                            },
                        });
                    },
                    {
                        isArchiving: result.loading,
                    },
                )
            }
        </ApolloMutation>
    );
};

export default ArchiveContentAssignmentsMutation;
